import { IconButton, Box, Popover, Typography } from "@mui/material";
import { ChevronLeft, ChevronRight, CalendarToday } from "@mui/icons-material";
import { styled } from "@mui/system";
import React, { useState, useEffect } from "react";
import { GhostButton as GS } from "../../sdk";

const GhostButton = styled(GS)`
  font: normal normal normal 18px/20px Roboto;
  cursor: pointer;
  min-width: 170px;
`;

const YearSwitch = styled(Box)`
  display: flex;
  justify-content: space-between;
  align-items: center;
  font: normal normal normal 18px/20px Roboto;
  padding: 0 8px;
`;

const MonthRow = styled(Box)`
  display: flex;
  justify-content: flex-start;
  padding: 0 8px;
  gap: 4px;
  font: normal normal normal 18px/20px Roboto;
`;

const MonthCellGhost = styled(GS)`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 8px;
  font: normal normal normal 14px/20px Roboto;
  min-width: 50px;
  cursor: pointer;
  border-radius: 4px;

  &:hover {
    opacity: 0.8;
  }
`;

const MonthCellPrimary = styled(MonthCellGhost)`
  background-color: rgb(48, 111, 188) !important;
  color: white !important;

  &:hover {
    border-radius: 2px;
    opacity: 0.9;
  }
`;

const MonthCellDisabled = styled(MonthCellGhost)`
  background-color: transparent;
  color: #6f6f6f;
  cursor: not-allowed;
  opacity: 0.5;
`;

const months = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

export default function MonthOnlyPicker({
  tableData,
  dataSetValue,
  setDataSetvalue,
}) {
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth());
  const [anchorEl, setAnchorEl] = useState(null);

  const hasTableData = tableData && tableData.length > 0;

  // Auto-select current month on mount if available
  useEffect(() => {
    if (hasTableData) {
      const currentMonthName = months[new Date().getMonth()];
      const currentYear = new Date().getFullYear();

      const currentMonthIndex = tableData.findIndex(
        (item) =>
          item.month.toLowerCase() === currentMonthName.toLowerCase() &&
          item.year === currentYear
      );

      if (currentMonthIndex !== -1) {
        setDataSetvalue(currentMonthIndex);
        setSelectedMonth(new Date().getMonth());
        setSelectedYear(currentYear);
      } else {
        // Select first available
        if (tableData.length > 0) {
          setDataSetvalue(0);
          const firstItem = tableData[0];
          setSelectedMonth(
            months.indexOf(
              firstItem.month.charAt(0).toUpperCase() +
                firstItem.month.slice(1).toLowerCase()
            )
          );
          setSelectedYear(firstItem.year);
        }
      }
    }
  }, [tableData, hasTableData]);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleYearChange = (year) => {
    setSelectedYear(year);
  };

  const handleMonthSelect = (monthIndex) => {
    const monthName = months[monthIndex];
    const index = tableData.findIndex(
      (item) =>
        item.month.toLowerCase() === monthName.toLowerCase() &&
        item.year === selectedYear
    );

    if (index !== -1) {
      setDataSetvalue(index);
      setSelectedMonth(monthIndex);
      handleClose();
    }
  };

  const isMonthAvailable = (monthIndex) => {
    const monthName = months[monthIndex];
    return tableData.some(
      (item) =>
        item.month.toLowerCase() === monthName.toLowerCase() &&
        item.year === selectedYear
    );
  };

  const open = Boolean(anchorEl);
  const displayMonth = hasTableData ? tableData[dataSetValue]?.month : "Month";
  const displayYear = selectedYear;

  const monthInChunks = React.useMemo(() => {
    const chunkSize = 4;
    return months
      .map((item, i) => (i % chunkSize ? [] : months.slice(i, i + chunkSize)))
      .filter((arr) => arr.length);
  }, []);

  return (
    <div>
      <GhostButton
        textTransform="none"
        variant="contained"
        color="primary"
        onClick={handleClick}
        style={{ backgroundColor: "#ffffff" }}
      >
        <Typography textTransform="none">{`${displayMonth} ${displayYear}`}</Typography>
        <CalendarToday
          sx={{
            cursor: "pointer",
            color: "rgb(48,111,188)",
            marginLeft: "8px",
            fontSize: "21px",
          }}
          onClick={handleClick}
        />
      </GhostButton>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "center",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "center",
        }}
      >
        <Box p={2} style={{ backgroundColor: "#ffffff" }}>
          {/* Year Navigation */}
          <YearSwitch>
            <IconButton onClick={() => handleYearChange(selectedYear - 1)}>
              <ChevronLeft />
            </IconButton>
            <Typography style={{ minWidth: "60px", textAlign: "center" }}>
              {selectedYear}
            </Typography>
            <IconButton onClick={() => handleYearChange(selectedYear + 1)}>
              <ChevronRight />
            </IconButton>
          </YearSwitch>

          {/* Month Grid */}
          {monthInChunks.map((monthArray, rowIndex) => (
            <MonthRow key={rowIndex}>
              {monthArray.map((mon, colIndex) => {
                const displayName = mon.slice(0, 3);
                const monthIndex = rowIndex * monthArray.length + colIndex;
                const available = isMonthAvailable(monthIndex);
                const isSelected =
                  selectedMonth === monthIndex && selectedYear === displayYear;

                if (!available) {
                  return (
                    <MonthCellDisabled key={displayName}>
                      <Typography textTransform="none">
                        {displayName}
                      </Typography>
                    </MonthCellDisabled>
                  );
                }

                if (isSelected) {
                  return (
                    <MonthCellPrimary
                      key={displayName}
                      onClick={() => handleMonthSelect(monthIndex)}
                    >
                      <Typography textTransform="none">
                        {displayName}
                      </Typography>
                    </MonthCellPrimary>
                  );
                }

                return (
                  <MonthCellGhost
                    key={displayName}
                    onClick={() => handleMonthSelect(monthIndex)}
                  >
                    <Typography textTransform="none">{displayName}</Typography>
                  </MonthCellGhost>
                );
              })}
            </MonthRow>
          ))}
        </Box>
      </Popover>
    </div>
  );
}
